import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project, Sprint, SprintStatus } from '../../../types';
import { getProjectSprints } from '../../../services/sprintService';
import { StandupEntry, getSprintStandups, createStandupEntry, updateStandupEntry } from '../../../services/standupService';
import ModuleGuard from '../../../components/ModuleGuard';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BM_LABEL, BM_TEXTAREA,
} from '../../../components/BoardModal';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

export default function StandupTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_agile">
      <StandupTabBody {...props} />
    </ModuleGuard>
  );
}

function StandupTabBody({ pca, projectId }: Props) {
  const [activeSprint, setActiveSprint] = useState<Sprint | null | undefined>(undefined);
  const [entries, setEntries] = useState<StandupEntry[]>([]);
  const [editing, setEditing] = useState<StandupEntry | 'new' | null>(null);
  const me = useCurrentUser(pca, projectId);

  const reload = async () => {
    const sprints = await getProjectSprints(pca, projectId);
    const active = sprints.find(s => s.dagrun_pm_status === SprintStatus.Active) ?? null;
    setActiveSprint(active);
    if (active) {
      const todayIso = new Date().toISOString();
      const list = await getSprintStandups(pca, active.dagrun_pm_sprintid, todayIso);
      setEntries(list);
    } else {
      setEntries([]);
    }
  };
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  if (activeSprint === undefined) {
    return <div style={{ padding: 80, textAlign: 'center', color: '#9198A8' }}>Loading…</div>;
  }
  if (!activeSprint) {
    return (
      <div style={{ padding: 80, textAlign: 'center', fontFamily: 'var(--font-ui)' }}>
        <div style={{ fontSize: 15, color: '#5A6272', marginBottom: 6 }}>No active sprint.</div>
        <div style={{ fontSize: 13, color: '#9198A8' }}>Set a sprint to Active in the Sprints tab to start daily standups.</div>
      </div>
    );
  }

  const myEntry = me ? entries.find(e => e.dagrun_pm_userid === me.oid) : null;

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ marginBottom: 18 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Daily Standup</h3>
        <div style={{ fontSize: 12, color: '#9198A8', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
          {activeSprint.dagrun_pm_name} — {activeSprint.dagrun_pm_startdate ? new Date(activeSprint.dagrun_pm_startdate).toLocaleDateString() : '—'}
          {' → '}
          {activeSprint.dagrun_pm_enddate ? new Date(activeSprint.dagrun_pm_enddate).toLocaleDateString() : '—'}
        </div>
      </div>

      {/* My standup today */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader>My Standup Today</SectionHeader>
        {!myEntry ? (
          <button type="button" onClick={() => setEditing('new')} disabled={!me}
            style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 8, opacity: me ? 1 : 0.5 }}
          >+ Add My Standup</button>
        ) : (
          <div style={{
            background: '#fff', border: '1px solid #EAECF0', borderRadius: 8, padding: 14, marginTop: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
              <button type="button" onClick={() => setEditing(myEntry)}
                style={{ background: 'transparent', border: '1px solid #DDE0E6', color: '#5A6272', padding: '3px 10px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >Edit</button>
            </div>
            <StandupBody entry={myEntry} />
          </div>
        )}
      </div>

      {/* Today's team standups */}
      <div>
        <SectionHeader>Today&rsquo;s Team Standups</SectionHeader>
        {entries.length === 0 ? (
          <div style={{ marginTop: 12, padding: 24, textAlign: 'center', color: '#9198A8', fontSize: 13 }}>No standup entries today yet.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12, marginTop: 12 }}>
            {entries.map(e => (
              <div key={e.dagrun_pm_standupentryid} style={{ background: '#fff', border: '1px solid #EAECF0', borderRadius: 8, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <Avatar name={e.dagrun_pm_username} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0F1117' }}>{e.dagrun_pm_username ?? '—'}</span>
                </div>
                <StandupBody entry={e} />
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && me && activeSprint && (
        <StandupModal
          pca={pca}
          projectId={projectId}
          sprintId={activeSprint.dagrun_pm_sprintid}
          meOid={me.oid}
          meName={me.displayName}
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{children}</div>
);

function StandupBody({ entry }: { entry: StandupEntry }) {
  return (
    <div>
      <Eyebrow>Yesterday</Eyebrow>
      <Body>{entry.dagrun_pm_yesterday ?? '—'}</Body>
      <Eyebrow>Today</Eyebrow>
      <Body>{entry.dagrun_pm_today ?? '—'}</Body>
      {entry.dagrun_pm_blockers && (
        <div style={{
          background: '#FEF3C7', borderLeft: '3px solid #D97706',
          color: '#92400E', padding: '8px 10px', borderRadius: 4,
          fontSize: 13, marginTop: 8, whiteSpace: 'pre-wrap',
        }}>
          <strong>Blocker:</strong> {entry.dagrun_pm_blockers}
        </div>
      )}
    </div>
  );
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 10, fontWeight: 600, color: '#9198A8', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 8 }}>{children}</div>
);
const Body = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 13, color: '#2C313D', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{children}</div>
);

const AVATAR_COLORS = ['#0E7490', '#7C3AED', '#92400E', '#065F46', '#991B1B'];
function Avatar({ name }: { name: string | null }) {
  const initials = (name ?? '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';
  let h = 0; for (const c of name ?? '') h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const bg = AVATAR_COLORS[h % AVATAR_COLORS.length];
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: bg, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 600,
    }}>{initials}</div>
  );
}

function StandupModal({ pca, projectId, sprintId, meOid, meName, existing, onClose, onSaved }: { pca: IPublicClientApplication; projectId: string; sprintId: string; meOid: string; meName: string; existing: StandupEntry | null; onClose: () => void; onSaved: () => void }) {
  const [yesterday, setYesterday] = useState(existing?.dagrun_pm_yesterday ?? '');
  const [today, setToday]         = useState(existing?.dagrun_pm_today ?? '');
  const [blockers, setBlockers]   = useState(existing?.dagrun_pm_blockers ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!yesterday.trim() || !today.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      if (existing) {
        await updateStandupEntry(pca, existing.dagrun_pm_standupentryid, {
          yesterday: yesterday.trim(), today: today.trim(), blockers: blockers.trim() || null,
        });
      } else {
        await createStandupEntry(pca, {
          sprintId, projectId, userId: meOid, userName: meName,
          yesterday: yesterday.trim(), today: today.trim(),
          blockers: blockers.trim() || null,
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Daily Standup" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !yesterday.trim() || !today.trim()}>{saving ? 'Saving…' : 'Save'}</BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Yesterday *</label>
        <textarea autoFocus value={yesterday} onChange={e => setYesterday(e.target.value)} style={BM_TEXTAREA} placeholder="What did you complete yesterday?" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Today *</label>
        <textarea value={today} onChange={e => setToday(e.target.value)} style={BM_TEXTAREA} placeholder="What will you work on today?" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Blockers</label>
        <textarea value={blockers} onChange={e => setBlockers(e.target.value)} style={BM_TEXTAREA} placeholder="Any blockers? Leave blank if none." />
      </div>
      {error && <div style={{ color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}
